import type { DraftState, TeamLiveData } from '../types';
import { ALL_TEAM_ABBRS } from '../constants/teams';
import { BASELINE_PROJECTIONS } from '../constants/teamProjections';

/**
 * TRANSPARENT VALUATION
 *
 * Every team gets a "suggested $" and every component is exposed in `breakdown`
 * so it can be displayed in a tooltip. Nothing is hardcoded — everything derives
 * from live ESPN data + your own draft archive + the league's current budget.
 *
 *   leagueBase   = totalRemainingBudget / teamsRemaining
 *                  (what each remaining team would cost if everyone paid average)
 *
 *   strength     = blend(projectedWins, lastSeasonWins)  → normalized 0..1
 *                  (uses ESPN FPI projected wins when available, otherwise live record)
 *
 *   strengthMult = 0.5 + 1.5 * strength
 *                  (range: 0.5x for worst team → 2.0x for best)
 *
 *   priorMult    = (last_year_price / leagueAvgPriorPrice)
 *                  Clamped to 0.5..2.0 so a single outlier season doesn't dominate.
 *
 *   suggested    = round( leagueBase * strengthMult * (LIVE_WEIGHT + PRIOR_WEIGHT * priorMult) )
 *
 *   We default to 70% live signal / 30% prior auction history.
 */

const LIVE_WEIGHT = 0.7;
const PRIOR_WEIGHT = 0.3;
const FALLBACK_BUDGET_PER_TEAM = 6.5; // 4 managers × 8 teams = 32 teams; ~$50/8 = $6.25

export interface ValuationBreakdown {
  leagueBase: number;        // $/team average for the rest of the draft
  strength: number;          // 0..1 normalized projection strength
  strengthMult: number;      // 0.5..2.0 multiplier
  priorPrice: number | null; // last year's price for this team (null if missing)
  leagueAvgPrior: number;    // avg of last year's prices across the available pool
  priorMult: number;         // 0.5..2.0 multiplier from prior price
  rawValue: number;          // pre-round dollar value
  suggested: number;         // rounded integer (>= minBid)
  source: 'fpi' | 'record' | 'fallback';
}

export interface TeamValuation extends ValuationBreakdown {
  abbr: string;
}

function clamp(n: number, lo: number, hi: number) { return Math.max(lo, Math.min(hi, n)); }

export function computeLeagueBase(state: DraftState): number {
  const remainingBudget = state.managers.reduce(
    (sum, m) => sum + Math.max(0, m.budget - Math.max(0, state.rosterCap - m.teamsDrafted - 1) * state.minBid),
    0,
  );
  const slotsLeft = state.managers.reduce(
    (sum, m) => sum + Math.max(0, state.rosterCap - m.teamsDrafted),
    0,
  );
  if (slotsLeft === 0) return FALLBACK_BUDGET_PER_TEAM;
  return remainingBudget / slotsLeft + state.minBid; // add minBid floor so avg is a real bid
}

function strengthFromLive(abbr: string, t: TeamLiveData | undefined, allTeams: TeamLiveData[]): {
  strength: number; source: 'fpi' | 'record' | 'fallback';
} {
  const base = BASELINE_PROJECTIONS[abbr];

  // In-season live record wins
  const r = t?.record;
  const games = (r?.wins ?? 0) + (r?.losses ?? 0) + (r?.ties ?? 0);
  if (games > 0) {
    const pct = ((r!.wins) + 0.5 * (r!.ties ?? 0)) / games;
    return { strength: clamp(pct, 0, 1), source: 'record' };
  }

  // Prefer ESPN FPI projected wins
  const proj = t?.projectedWins ?? base?.projectedWins;
  if (proj !== undefined) {
    // 5.0 to 12.0 wins span across NFL
    return { strength: clamp((proj - 5.0) / 7.0, 0, 1), source: 'fpi' };
  }

  return { strength: 0.5, source: 'fallback' };
}

export function computeValuations(
  state: DraftState,
  liveData: Record<string, TeamLiveData>,
  priorPrices: Record<string, number>,
): Record<string, TeamValuation> {
  const leagueBase = computeLeagueBase(state);
  const liveList = Object.values(liveData);

  // League average prior price across the AVAILABLE pool (so once stars sell, the
  // average rebalances and remaining values rise correctly)
  const priorVals = state.availableTeams
    .map(abbr => priorPrices[abbr])
    .filter((v): v is number => typeof v === 'number' && v > 0);
  const leagueAvgPrior = priorVals.length
    ? priorVals.reduce((a, b) => a + b, 0) / priorVals.length
    : leagueBase;

  const out: Record<string, TeamValuation> = {};
  for (const abbr of ALL_TEAM_ABBRS) {
    const live = liveData[abbr];
    const { strength, source } = strengthFromLive(abbr, live, liveList);
    const strengthMult = 0.5 + 1.5 * strength;

    const priorPrice = priorPrices[abbr] ?? null;
    const priorMult = priorPrice != null && leagueAvgPrior > 0
      ? clamp(priorPrice / leagueAvgPrior, 0.5, 2.0)
      : 1.0;

    const rawValue = leagueBase * strengthMult * (LIVE_WEIGHT + PRIOR_WEIGHT * priorMult);
    const suggested = Math.max(state.minBid, Math.round(rawValue));

    out[abbr] = {
      abbr, leagueBase, strength, strengthMult, priorPrice, leagueAvgPrior,
      priorMult, rawValue, suggested, source,
    };
  }
  return out;
}

/** Value rating vs a current/proposed bid. */
export function valueRating(suggested: number, bid: number): {
  label: 'BARGAIN' | 'FAIR' | 'OVERPAY';
  ratio: number;
  /** Tailwind background class (static — JIT-safe). */
  bgClass: string;
  /** Tailwind text class. */
  textClass: string;
} {
  if (bid <= 0) {
    return { label: 'FAIR', ratio: 1, bgClass: 'bg-amber-600', textClass: 'text-amber-300' };
  }
  const ratio = bid / Math.max(1, suggested);
  if (ratio <= 0.8) {
    return { label: 'BARGAIN', ratio, bgClass: 'bg-emerald-600', textClass: 'text-emerald-300' };
  }
  if (ratio >= 1.2) {
    return { label: 'OVERPAY', ratio, bgClass: 'bg-rose-600', textClass: 'text-rose-300' };
  }
  return { label: 'FAIR', ratio, bgClass: 'bg-amber-600', textClass: 'text-amber-300' };
}
